import { z } from 'zod';

import { mapEnumToOptions } from '@seller-area/utils/mappers';
import { formatDateFromString } from '@seller-area/utils/dayjs';
import { isDocIncomplete, refineChangedDocNumber } from '@seller-area/utils/document-utils';
import { GroupDoc } from '@seller-area/contexts/commercial-group';

import { UnitPayloadRequestSchema } from '@seller-area/hooks/units/mutations/create-unit/types';
import { UnitSchemaProps } from '@seller-area/hooks/units/queries/get-unit-by-id/schema';

import { isAModality, isSubgroupValidForModality, isUnitTypeValidForCascade, showsDemandPeak } from './tariff-cascade';
import {
  isBlankDateInput,
  isMigratedFromMigrationDate,
  mapMigradoFieldsToPayload,
  toIsoDateFromDdMmYyyy,
} from './map-migrado-fields';

export function formatPostalCodeMask(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

export enum UnitStatusEnum {
  LEAD = 'Lead',
  CUSTOMER = 'Cliente',
  CHURN = 'Churn',
}

export enum UnitTypeEnumKey {
  COMMERCIAL = 'Comercial',
  RURAL = 'Rural',
  INDUSTRIAL = 'Industrial',
  RESIDENTIAL = 'Residencial',
}

export enum ProductTypeEnum {
  CLARKE_MANAGEMENT = 'Gestão Clarke',
  CLARKE_RETAIL = 'Varejista Clarke',
  RETAIL_TRANSFER = 'Tombamento Varejista',
  WHOLESALE_TRANSFER = 'Tombamento Atacadista',
}

export enum EnergyContractingModelEnum {
  WHOLESALE = 'Atacadista',
  RETAIL = 'Varejista',
}

export const energyContractingModelOptions = mapEnumToOptions(EnergyContractingModelEnum);

export const unitStatusOptions = mapEnumToOptions(UnitStatusEnum);

const unitStatusKeys = Object.keys(UnitStatusEnum) as [
  keyof typeof UnitStatusEnum,
  ...Array<keyof typeof UnitStatusEnum>,
];

const unitStatusSchema = z.enum(unitStatusKeys, {
  message: 'Status é obrigatório',
});

export const unitTypeOptions = mapEnumToOptions(UnitTypeEnumKey);

const isValidFormDate = (value?: string | null) => isBlankDateInput(value) || Boolean(toIsoDateFromDdMmYyyy(value));

const docTypeSchema = z.enum(['CNPJ', 'CPF'], {
  message: 'Tipo de documento é obrigatório',
});

const createUnitFormBaseSchema = z.object({
  companyId: z.string().nullish(),
  installationCode: z.string().nullish(),
  newInstallationCode: z.string().nullish(),
  edcCustomerCode: z.string().nullish(),
  edcPortalLogin: z.string().nullish(),
  edcPortalPassword: z.string().nullish(),
  isNewConnection: z.boolean().default(false),
  name: z.string().min(1, 'Nome fantasia é obrigatório'),
  docType: docTypeSchema,
  docNumber: z.string().min(1, 'Número de documento é obrigatório'),
  legalName: z.string().min(1, 'Razão social é obrigatória'),
  unitStatus: unitStatusSchema,
  clientSince: z
    .string()
    .nullish()
    .refine((v) => isValidFormDate(v), 'Data inválida'),
  churnDate: z
    .string()
    .nullish()
    .refine((v) => isValidFormDate(v), 'Data inválida'),
  fullTextAddress: z.string().trim().min(1, 'Endereço é obrigatório'),
  postalCode: z
    .string()
    .trim()
    .min(1, 'CEP é obrigatório')
    .refine((value) => value.replace(/\D/g, '').length === 8, 'CEP deve ter 8 dígitos'),
  city: z.string().trim().min(1, 'Cidade é obrigatória'),

  edc: z.number().nullable().optional(),
  state: z.string().min(1, 'Estado é obrigatório'),
  migrationDate: z
    .string()
    .nullish()
    .refine((v) => isValidFormDate(v), 'Data inválida'),
  isMigrated: z
    .boolean({
      required_error: 'Migrado é obrigatório',
      invalid_type_error: 'Migrado é obrigatório',
    })
    .nullable()
    .refine((value: boolean | null): boolean => value !== null, { message: 'Migrado é obrigatório' }),
  energyContractingModel: z.preprocess(
    (value) => (value === '' || value === undefined ? null : value),
    z.enum(['WHOLESALE', 'RETAIL']).nullable(),
  ),
  contractedDemandPeak: z.number().nullable().optional(),
  contractedDemandOffPeak: z.number().nullable().optional(),
  contractedDemandPeakBeforeMigration: z.number().nullable().optional(),
  contractedDemandOffPeakBeforeMigration: z.number().nullable().optional(),
  contractedDemandPeakBeforeLaw: z.number().nullable().optional(),
  contractedDemandOffPeakBeforeLaw: z.number().nullable().optional(),
  volumeMwm: z.number().nullable().optional(),
  tariffSubgroup: z.enum(['A1', 'A2', 'A3', 'A3A', 'A4', 'AS', 'B1', 'B2', 'B3'], {
    message: 'Subgrupo tarifário é obrigatório',
  }),
  tariffModality: z.enum(['GREEN', 'BLUE', 'B3_OPTING', 'B3_STANDARD'], {
    message: 'Modalidade tarifária é obrigatória',
  }),
  unitType: z.enum(['COMMERCIAL', 'RURAL', 'INDUSTRIAL', 'RESIDENTIAL'], {
    message: 'Tipo tarifário é obrigatório',
  }),
  submarket: z.enum(['NORTH', 'NORTHEAST', 'SOUTH', 'SOUTHEAST'], {
    message: 'Submercado é obrigatório',
  }),
  powerGenerator: z.boolean().default(false),

  aclBefore2025Law: z
    .boolean({
      required_error: 'Estava no ACL antes da Lei 15.269/2025 é obrigatório',
      invalid_type_error: 'Estava no ACL antes da Lei 15.269/2025 é obrigatório',
    })
    .nullable()
    .refine((value: boolean | null): boolean => value !== null, {
      message: 'Estava no ACL antes da Lei 15.269/2025 é obrigatório',
    }),
  managementCost: z.number().nullable().optional(),
  expectedMigrationDate: z
    .string()
    .nullish()
    .refine((v) => isValidFormDate(v), 'Data inválida'),
});

function refineUnitDocNumber(
  data: z.infer<typeof createUnitFormBaseSchema>,
  ctx: z.RefinementCtx,
  initialDocNumber: string | null | undefined,
) {
  const docType = data.docType as keyof typeof GroupDoc;
  if (isDocIncomplete(docType, data.docNumber)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['docNumber'],
      message: docType === 'CPF' ? 'CPF incompleto' : 'CNPJ incompleto',
    });
    return;
  }
  refineChangedDocNumber(initialDocNumber)({ docType, docNumber: data.docNumber }, ctx);
}

function refineUnitForm(data: z.infer<typeof createUnitFormBaseSchema>, ctx: z.RefinementCtx) {
  if (!data.isNewConnection) {
    const hasAneelCode = Boolean(data.newInstallationCode?.trim());
    if (!hasAneelCode) {
      if (!data.installationCode?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['installationCode'],
          message: 'Número de instalação é obrigatório',
        });
      }
      if (!data.edcCustomerCode?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['edcCustomerCode'],
          message: 'Número do cliente é obrigatório',
        });
      }
    }
  }

  if (!data.edc) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['edc'],
      message: 'Distribuidora é obrigatória',
    });
  }

  if (data.unitStatus !== 'LEAD' && isBlankDateInput(data.clientSince)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['clientSince'],
      message: 'Cliente desde é obrigatório',
    });
  }

  if (data.unitStatus === 'CHURN' && isBlankDateInput(data.churnDate)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['churnDate'],
      message: 'Data de churn é obrigatória',
    });
  }

  if (!isSubgroupValidForModality(data.tariffModality, data.tariffSubgroup)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['tariffSubgroup'],
      message: 'Subgrupo inválido para a modalidade selecionada',
    });
  }

  if (!isUnitTypeValidForCascade(data.tariffModality, data.tariffSubgroup, data.unitType)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['unitType'],
      message: 'Tipo tarifário inválido para o subgrupo selecionado',
    });
  }

  if (data.isMigrated) {
    if (isBlankDateInput(data.migrationDate)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['migrationDate'],
        message: 'Data de migração realizada é obrigatória',
      });
    }
    if (!data.energyContractingModel) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['energyContractingModel'],
        message: 'Modelo de contratação é obrigatório quando migrado',
      });
    }

    if (isAModality(data.tariffModality) && !data.isNewConnection) {
      if (data.contractedDemandOffPeakBeforeMigration == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['contractedDemandOffPeakBeforeMigration'],
          message: 'Demanda FP antes da migração é obrigatória',
        });
      }
      if (showsDemandPeak(data.tariffModality) && data.contractedDemandPeakBeforeMigration == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['contractedDemandPeakBeforeMigration'],
          message: 'Demanda P antes da migração é obrigatória',
        });
      }
    }
  }

  if (isBlankDateInput(data.expectedMigrationDate)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['expectedMigrationDate'],
      message: 'Data de migração estimada é obrigatória',
    });
  }

  // Ligação nova: sem histórico → demandas calculadas ficam vazias e não são obrigatórias
  // (alinhado a UnitPayloadValidator.validateContractedDemands).
  if (isAModality(data.tariffModality) && !data.isNewConnection) {
    if (data.contractedDemandOffPeak == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contractedDemandOffPeak'],
        message: 'Demanda contratada FP é obrigatória',
      });
    }
    if (showsDemandPeak(data.tariffModality) && data.contractedDemandPeak == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contractedDemandPeak'],
        message: 'Demanda contratada P é obrigatória',
      });
    }
    if (data.contractedDemandOffPeakBeforeLaw == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contractedDemandOffPeakBeforeLaw'],
        message: 'Demanda FP com desconto é obrigatória',
      });
    }
    if (showsDemandPeak(data.tariffModality) && data.contractedDemandPeakBeforeLaw == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contractedDemandPeakBeforeLaw'],
        message: 'Demanda P com desconto é obrigatória',
      });
    }
  }
}

function refineUnitFormWithCompany(data: z.infer<typeof createUnitFormBaseSchema>, ctx: z.RefinementCtx) {
  refineUnitForm(data, ctx);

  if (!data.companyId?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['companyId'],
      message: 'Empresa vinculada é obrigatória',
    });
  }
}

export const buildUnitFormSchema = ({
  withCompany,
  initialDocNumber,
}: {
  withCompany: boolean;
  initialDocNumber?: string | null;
}) =>
  createUnitFormBaseSchema.superRefine((data, ctx) => {
    refineUnitDocNumber(data, ctx, initialDocNumber);
    if (withCompany) refineUnitFormWithCompany(data, ctx);
    else refineUnitForm(data, ctx);
  });

export const createUnitFormSchema = buildUnitFormSchema({ withCompany: false });

export const unitFormWithCompanySchema = buildUnitFormSchema({ withCompany: true });

export type CreateUnitFormData = z.infer<typeof createUnitFormBaseSchema>;
export type UnitFormData = z.infer<typeof unitFormWithCompanySchema>;

export const defaultValues: Partial<CreateUnitFormData> = {
  isNewConnection: false,
  isMigrated: null,
  energyContractingModel: null,
  powerGenerator: false,
  aclBefore2025Law: null,
  docType: 'CNPJ',
  docNumber: '',
  name: '',
  legalName: '',
  fullTextAddress: '',
  postalCode: '',
  city: '',
  unitStatus: undefined,
};

export const editDefaultValues: Partial<UnitFormData> = {
  ...defaultValues,
};

const ddmmyyyyToYyyymmdd = toIsoDateFromDdMmYyyy;

export function mapUnitToFormData(unit: UnitSchemaProps): Partial<UnitFormData> {
  const { unitStatus: _defaultUnitStatus, ...sharedDefaults } = defaultValues;

  return {
    ...sharedDefaults,
    companyId: unit.company?.id ?? undefined,
    name: unit.name ?? '',
    legalName: unit.legalName ?? '',
    docType: unit.docType ?? 'CNPJ',
    docNumber: unit.docNumber ?? '',
    installationCode: unit.installationCode ?? '',
    newInstallationCode: unit.newInstallationCode ?? '',
    fullTextAddress: unit.fullTextAddress ?? '',
    postalCode: unit.postalCode ? formatPostalCodeMask(unit.postalCode) : '',
    state: unit.state ?? '',
    city: unit.city ?? '',
    edcCustomerCode: unit.edcCustomerCode ?? '',
    edcPortalLogin: unit.edcPortalLogin ?? '',
    edcPortalPassword: unit.edcPortalPassword ?? '',
    unitStatus: unit.status ?? undefined,
    clientSince:
      unit.clientSince && !isBlankDateInput(unit.clientSince)
        ? formatDateFromString(unit.clientSince, 'YYYY-MM-DD', 'DDMMYYYY')
        : undefined,
    churnDate:
      unit.churnDate && !isBlankDateInput(unit.churnDate)
        ? formatDateFromString(unit.churnDate, 'YYYY-MM-DD', 'DDMMYYYY')
        : undefined,
    edc: unit.edc?.id ? Number(unit.edc.id) : undefined,
    tariffSubgroup: (unit.tariffSubgroup as UnitFormData['tariffSubgroup']) ?? undefined,
    tariffModality:
      unit.tariffModality === 'B3_COMMERCIAL'
        ? 'B3_STANDARD'
        : (unit.tariffModality as UnitFormData['tariffModality']) ?? undefined,
    submarket: unit.submarket ?? undefined,
    unitType: unit.unitType ?? undefined,
    contractedDemandPeak: unit.contractedDemandPeak ?? undefined,
    contractedDemandOffPeak: unit.contractedDemandOffPeak ?? undefined,
    contractedDemandPeakBeforeMigration: unit.contractedDemandPeakBeforeMigration ?? undefined,
    contractedDemandOffPeakBeforeMigration: unit.contractedDemandOffPeakBeforeMigration ?? undefined,
    contractedDemandPeakBeforeLaw: unit.contractedDemandPeakBeforeLaw ?? undefined,
    contractedDemandOffPeakBeforeLaw: unit.contractedDemandOffPeakBeforeLaw ?? undefined,
    volumeMwm: unit.volumeMwm ?? undefined,
    migrationDate:
      unit.migrationDate && !isBlankDateInput(unit.migrationDate)
        ? formatDateFromString(unit.migrationDate, 'YYYY-MM-DD', 'DDMMYYYY')
        : undefined,
    expectedMigrationDate:
      unit.expectedMigrationDate && !isBlankDateInput(unit.expectedMigrationDate)
        ? formatDateFromString(unit.expectedMigrationDate, 'YYYY-MM-DD', 'DDMMYYYY')
        : undefined,
    isMigrated: isMigratedFromMigrationDate(unit.migrationDate),
    energyContractingModel:
      unit.energyContractingModel === 'WHOLESALE' || unit.energyContractingModel === 'RETAIL'
        ? unit.energyContractingModel
        : undefined,
    powerGenerator: unit.powerGenerator ?? false,
    isNewConnection: unit.isNewConnection ?? false,
    aclBefore2025Law: unit.aclBefore2025Law ?? false,
    managementCost: unit.managementCost ?? undefined,
  };
}

export function mapFormDataToUnitPayload(data: UnitFormData): UnitPayloadRequestSchema {
  return {
    name: data.name,
    legalName: data.legalName,
    docType: data.docType,
    docNumber: data.docNumber,
    installationCode: data.installationCode || undefined,
    newInstallationCode: data.newInstallationCode || undefined,
    edcCustomerCode: data.edcCustomerCode || undefined,
    edcPortalLogin: data.edcPortalLogin || undefined,
    edcPortalPassword: data.edcPortalPassword || undefined,
    city: data.city,
    unitType: data.unitType,
    submarket: data.submarket,
    state: data.state,
    fullTextAddress: data.fullTextAddress,
    postalCode: data.postalCode.replace(/\D/g, ''),
    edc: data.edc ?? undefined,
    tariffSubgroup: data.tariffSubgroup ?? undefined,
    tariffModality: data.tariffModality,
    contractedDemandOffPeak: data.contractedDemandOffPeak ?? undefined,
    contractedDemandPeak: data.contractedDemandPeak ?? undefined,
    contractedDemandOffPeakBeforeMigration: data.contractedDemandOffPeakBeforeMigration ?? undefined,
    contractedDemandPeakBeforeMigration: data.contractedDemandPeakBeforeMigration ?? undefined,
    contractedDemandOffPeakBeforeLaw: data.contractedDemandOffPeakBeforeLaw ?? undefined,
    contractedDemandPeakBeforeLaw: data.contractedDemandPeakBeforeLaw ?? undefined,
    volumeMwm: data.volumeMwm ?? undefined,
    ...mapMigradoFieldsToPayload(
      {
        isMigrated: data.isMigrated === true,
        migrationDate: data.migrationDate,
        energyContractingModel: data.energyContractingModel,
      },
      ddmmyyyyToYyyymmdd,
    ),
    expectedMigrationDate: ddmmyyyyToYyyymmdd(data.expectedMigrationDate) ?? null,
    clientSince: ddmmyyyyToYyyymmdd(data.clientSince),
    churnDate: ddmmyyyyToYyyymmdd(data.churnDate),
    powerGenerator: data.powerGenerator,
    isNewConnection: data.isNewConnection,
    status: data.unitStatus,
    productType: 'CLARKE_MANAGEMENT',
    aclBefore2025Law: Boolean(data.aclBefore2025Law),
    managementCost: data.managementCost ?? null,
  };
}
